<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ProjectsAPI from 'dashboard/api/projects';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CreateProjectModal from './CreateProjectModal.vue';
import ProjectPhaseRow from './ProjectPhaseRow.vue';
import ProjectOverview from './ProjectOverview.vue';
import ProjectBoard from './ProjectBoard.vue';
import ProjectDeliverables from './ProjectDeliverables.vue';
import ProjectMilestones from './ProjectMilestones.vue';
import ProjectDocuments from './ProjectDocuments.vue';
import ProjectPeople from './ProjectPeople.vue';
import ProjectRisksIssues from './ProjectRisksIssues.vue';
import ProjectEmptyState from './ProjectEmptyState.vue';

const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const editModalRef = ref(null);
const activeTab = ref('overview');

// Phase to pre-select when the Board tab is opened from a phase row.
const boardPhaseId = ref(null);

// Inline phase create + playbook picker state.
const newPhaseName = ref('');
const isAddingPhase = ref(false);
const showPlaybookPicker = ref(false);
const selectedPlaybookId = ref(null);
const isApplyingPlaybook = ref(false);

const STATUS_CLASS = {
  draft: 'bg-n-alpha-1 text-n-slate-11',
  active: 'bg-n-teal-3 text-n-teal-11',
  on_hold: 'bg-n-amber-3 text-n-amber-11',
  completed: 'bg-n-blue-3 text-n-blue-11',
  cancelled: 'bg-n-ruby-3 text-n-ruby-11',
};

const project = computed(() => store.getters.getProjectById(props.projectId));
const playbooks = computed(() => store.getters.getPlaybooks);

const phases = computed(() => {
  const list = project.value?.phases || [];
  return [...list].sort((a, b) => (a.position || 0) - (b.position || 0));
});

const statusKey = computed(() => project.value?.status || 'draft');
const statusClass = computed(
  () => STATUS_CLASS[statusKey.value] || STATUS_CLASS.draft
);
const statusLabel = computed(() =>
  t(`PROJECTS.STATUS.${statusKey.value.toUpperCase()}`)
);

const progress = computed(() => {
  const value = Number(project.value?.progress) || 0;
  return Math.max(0, Math.min(100, Math.round(value)));
});

const displayId = computed(
  () => `PRJ-${project.value?.display_id ?? project.value?.id}`
);

const targetDate = computed(() => project.value?.target_end_date || '');

// Overview metric strip: aggregate counts come from the show payload's summary.
const summary = computed(() => project.value?.summary || {});

const daysToTarget = computed(() => {
  if (!targetDate.value) return null;
  const target = new Date(targetDate.value);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round((target - today) / msPerDay);
});

const metrics = computed(() => {
  const s = summary.value;
  return [
    {
      key: 'deliverables',
      icon: 'i-lucide-package',
      label: t('PROJECTS.TABS.DELIVERABLES'),
      value: `${s.deliverables_accepted ?? 0}/${s.deliverables_total ?? 0}`,
    },
    {
      key: 'milestones',
      icon: 'i-lucide-flag',
      label: t('PROJECTS.TABS.MILESTONES'),
      value: `${s.milestones_achieved ?? 0}/${s.milestones_total ?? 0}`,
    },
    {
      key: 'risks',
      icon: 'i-lucide-triangle-alert',
      label: t('PROJECTS.OVERVIEW.OPEN_RISKS'),
      value: String(s.open_risks ?? 0),
    },
    {
      key: 'target',
      icon: 'i-lucide-calendar-clock',
      label: t('PROJECTS.OVERVIEW.DAYS_TO_TARGET'),
      value:
        daysToTarget.value === null
          ? '—'
          : t('PROJECTS.OVERVIEW.DAYS', { count: daysToTarget.value }),
    },
  ];
});

const tabs = computed(() => [
  {
    key: 'overview',
    label: t('PROJECTS.TABS.OVERVIEW'),
    icon: 'i-lucide-layout-dashboard',
  },
  {
    key: 'phases',
    label: t('PROJECTS.TABS.PHASES'),
    icon: 'i-lucide-list-ordered',
  },
  { key: 'board', label: t('PROJECTS.TABS.BOARD'), icon: 'i-lucide-kanban' },
  {
    key: 'deliverables',
    label: t('PROJECTS.TABS.DELIVERABLES'),
    icon: 'i-lucide-package',
    count: summary.value.deliverables_total,
  },
  {
    key: 'milestones',
    label: t('PROJECTS.TABS.MILESTONES'),
    icon: 'i-lucide-flag',
    count: summary.value.milestones_total,
  },
  {
    key: 'people',
    label: t('PROJECTS.TABS.PEOPLE'),
    icon: 'i-lucide-users',
    count:
      (project.value?.members_count || 0) +
      (project.value?.contacts_count || 0),
  },
  {
    key: 'risks',
    label: t('PROJECTS.TABS.RISKS'),
    icon: 'i-lucide-triangle-alert',
    count: summary.value.open_risks,
  },
  {
    key: 'documents',
    label: t('PROJECTS.TABS.DOCUMENTS'),
    icon: 'i-lucide-file-text',
  },
]);

const loadProject = () =>
  store.dispatch('fetchProject', { id: props.projectId });

const loadPlaybooks = () => {
  if (!playbooks.value.length) store.dispatch('fetchPlaybooks');
};

const deleteProject = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PROJECTS.DELETE_CONFIRM'))) return;
  await store.dispatch('deleteProject', props.projectId);
  router.push({ name: 'projects_list' });
};

const openBoardForPhase = phase => {
  boardPhaseId.value = phase?.id ?? null;
  activeTab.value = 'board';
};

// Notes and status reports now live inside the Documents tab; redirect any
// legacy navigate targets there so Overview's recent-activity links keep working.
const goToTab = tab => {
  activeTab.value = ['notes', 'status_reports'].includes(tab)
    ? 'documents'
    : tab;
};

const createPhase = async () => {
  const name = newPhaseName.value.trim();
  if (!name || isAddingPhase.value) return;
  isAddingPhase.value = true;
  try {
    await ProjectsAPI.createPhase(props.projectId, { name });
    newPhaseName.value = '';
    await loadProject();
  } catch (error) {
    useAlert(t('PROJECTS.PHASES.ERROR'));
  } finally {
    isAddingPhase.value = false;
  }
};

const togglePlaybookPicker = () => {
  showPlaybookPicker.value = !showPlaybookPicker.value;
  if (showPlaybookPicker.value) loadPlaybooks();
};

const applyPlaybook = async () => {
  if (!selectedPlaybookId.value || isApplyingPlaybook.value) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PROJECTS.PLAYBOOKS.APPLY_CONFIRM'))) return;
  isApplyingPlaybook.value = true;
  try {
    await store.dispatch('applyPlaybook', {
      id: props.projectId,
      playbookId: Number(selectedPlaybookId.value),
    });
    useAlert(t('PROJECTS.PLAYBOOKS.APPLY_SUCCESS'));
    showPlaybookPicker.value = false;
    selectedPlaybookId.value = null;
    await loadProject();
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.APPLY_ERROR'));
  } finally {
    isApplyingPlaybook.value = false;
  }
};

onMounted(() => {
  loadProject();
  loadPlaybooks();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <div v-if="project" class="flex-1 min-h-0 overflow-y-auto">
      <div class="flex flex-col w-full gap-6 px-6 py-6 lg:px-8">
        <!-- Back -->
        <Button
          variant="link"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          :label="t('PROJECTS.ACTIONS.BACK')"
          class="self-start"
          @click="router.push({ name: 'projects_list' })"
        />

        <!-- Header -->
        <header class="flex items-start justify-between gap-4">
          <div class="flex items-center min-w-0 gap-3">
            <Avatar :name="project.name" :size="48" />
            <div class="flex flex-col min-w-0 gap-1">
              <h1 class="text-xl font-semibold truncate text-n-slate-12">
                {{ project.name }}
              </h1>
              <div class="flex items-center gap-3">
                <span class="font-mono text-xs text-n-slate-10">
                  {{ displayId }}
                </span>
                <span
                  class="inline-flex items-center px-2 py-0.5 text-xs rounded-md"
                  :class="statusClass"
                >
                  {{ statusLabel }}
                </span>
              </div>
            </div>
          </div>
          <p
            v-if="project.description"
            :title="project.description"
            class="self-center flex-1 hidden min-w-0 text-sm leading-relaxed line-clamp-3 text-n-slate-11 lg:block"
          >
            {{ project.description }}
          </p>
          <div class="flex items-center gap-2 shrink-0">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-pencil"
              :label="t('PROJECTS.ACTIONS.EDIT')"
              @click="editModalRef?.open()"
            />
            <Button
              variant="ghost"
              color="ruby"
              size="sm"
              icon="i-lucide-trash-2"
              :label="t('PROJECTS.ACTIONS.DELETE')"
              @click="deleteProject"
            />
          </div>
        </header>

        <!-- Metric strip -->
        <section
          class="grid grid-cols-2 divide-y border rounded-xl divide-n-weak border-n-weak bg-n-solid-2 sm:grid-cols-5 sm:divide-y-0 sm:divide-x"
        >
          <!-- Progress -->
          <div class="flex flex-col gap-1.5 p-4">
            <span class="flex items-center gap-1.5 text-xs text-n-slate-10">
              <span class="i-lucide-trending-up size-3.5" />
              {{ t('PROJECTS.OVERVIEW.PROGRESS') }}
            </span>
            <div class="flex items-center gap-2">
              <div
                class="flex-1 h-1.5 overflow-hidden rounded-full bg-n-alpha-2"
              >
                <div
                  class="h-full rounded-full bg-n-brand"
                  :style="{ width: `${progress}%` }"
                />
              </div>
              <span class="text-sm font-medium tabular-nums text-n-slate-12">
                {{ progress }}%
              </span>
            </div>
          </div>
          <!-- Counts -->
          <div
            v-for="metric in metrics"
            :key="metric.key"
            class="flex flex-col gap-1.5 p-4"
          >
            <span class="flex items-center gap-1.5 text-xs text-n-slate-10">
              <span :class="metric.icon" class="size-3.5" />
              {{ metric.label }}
            </span>
            <span class="text-sm font-medium tabular-nums text-n-slate-12">
              {{ metric.value }}
            </span>
          </div>
        </section>

        <!-- Body: full-width main tabs -->
        <div class="flex flex-col gap-6">
          <!-- Main -->
          <div class="flex flex-col flex-1 min-w-0 gap-6">
            <section
              class="flex flex-col border rounded-xl border-n-weak bg-n-solid-2"
            >
              <nav
                class="flex items-center gap-1 px-2 overflow-x-auto border-b border-n-weak"
              >
                <button
                  v-for="tab in tabs"
                  :key="tab.key"
                  type="button"
                  class="flex items-center gap-1.5 px-3 h-10 -mb-px text-sm border-b-2 transition-colors shrink-0"
                  :class="
                    activeTab === tab.key
                      ? 'border-n-brand text-n-slate-12 font-medium'
                      : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
                  "
                  @click="activeTab = tab.key"
                >
                  <span :class="tab.icon" class="size-4" />
                  {{ tab.label }}
                  <span
                    v-if="tab.count"
                    class="px-1.5 py-0.5 text-xs rounded-md tabular-nums bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ tab.count }}
                  </span>
                </button>
              </nav>

              <div class="p-4 sm:p-5">
                <!-- Overview tab -->
                <ProjectOverview
                  v-if="activeTab === 'overview'"
                  :project="project"
                  :project-id="projectId"
                  @navigate="goToTab"
                  @open-phase="openBoardForPhase"
                />

                <!-- Phases tab -->
                <div
                  v-show="activeTab === 'phases'"
                  class="flex flex-col gap-3"
                >
                  <!-- Apply playbook -->
                  <div class="flex items-center justify-between gap-2">
                    <h3 class="text-sm font-medium text-n-slate-12">
                      {{ t('PROJECTS.PHASES.TITLE') }}
                    </h3>
                    <Button
                      variant="faded"
                      color="slate"
                      size="sm"
                      icon="i-lucide-book-open"
                      :label="t('PROJECTS.PLAYBOOKS.APPLY')"
                      @click="togglePlaybookPicker"
                    />
                  </div>

                  <div
                    v-if="showPlaybookPicker"
                    class="flex items-center gap-2 p-3 border rounded-xl border-n-weak bg-n-solid-2"
                  >
                    <select
                      v-model="selectedPlaybookId"
                      class="flex-1 min-w-0 h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
                    >
                      <option :value="null">
                        {{ t('PROJECTS.PLAYBOOKS.PICKER_PLACEHOLDER') }}
                      </option>
                      <option
                        v-for="pb in playbooks"
                        :key="pb.id"
                        :value="pb.id"
                      >
                        {{ pb.name }}
                      </option>
                    </select>
                    <Button
                      size="sm"
                      color="blue"
                      :label="t('PROJECTS.PLAYBOOKS.APPLY')"
                      :is-loading="isApplyingPlaybook"
                      :disabled="!selectedPlaybookId"
                      @click="applyPlaybook"
                    />
                  </div>

                  <!-- Phase list -->
                  <ProjectEmptyState
                    v-if="!phases.length"
                    compact
                    icon="i-lucide-list-ordered"
                    :title="t('PROJECTS.PHASES.EMPTY')"
                  />
                  <div v-else class="flex flex-col divide-y divide-n-weak/60">
                    <button
                      v-for="(phase, index) in phases"
                      :key="phase.id"
                      type="button"
                      class="text-left rounded-md hover:bg-n-alpha-1"
                      :title="t('PROJECTS.PHASES.OPEN_BOARD')"
                      @click="openBoardForPhase(phase)"
                    >
                      <ProjectPhaseRow :phase="phase" :index="index" />
                    </button>
                  </div>

                  <!-- Inline add phase -->
                  <div class="flex items-center gap-2 pt-1">
                    <input
                      v-model="newPhaseName"
                      type="text"
                      :placeholder="t('PROJECTS.PHASES.NAME_PLACEHOLDER')"
                      class="flex-1 min-w-0 h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
                      @keyup.enter="createPhase"
                    />
                    <Button
                      icon="i-lucide-plus"
                      size="sm"
                      color="blue"
                      :label="t('PROJECTS.PHASES.ADD')"
                      :is-loading="isAddingPhase"
                      :disabled="!newPhaseName.trim()"
                      @click="createPhase"
                    />
                  </div>
                </div>

                <!-- Board tab -->
                <ProjectBoard
                  v-if="activeTab === 'board'"
                  :project-id="projectId"
                  :phases="phases"
                  :initial-phase-id="boardPhaseId"
                />

                <!-- Deliverables tab -->
                <ProjectDeliverables
                  v-if="activeTab === 'deliverables'"
                  :project-id="projectId"
                  :phases="phases"
                />

                <!-- Milestones tab -->
                <ProjectMilestones
                  v-if="activeTab === 'milestones'"
                  :project-id="projectId"
                  :phases="phases"
                />

                <!-- People tab (team + stakeholders) -->
                <ProjectPeople
                  v-if="activeTab === 'people'"
                  :project-id="projectId"
                />

                <!-- Risks & issues tab -->
                <ProjectRisksIssues
                  v-if="activeTab === 'risks'"
                  :project-id="projectId"
                />

                <!-- Documents tab (also hosts status reports + notes) -->
                <ProjectDocuments
                  v-if="activeTab === 'documents'"
                  :project-id="projectId"
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>

    <CreateProjectModal
      ref="editModalRef"
      :project="project"
      @updated="loadProject"
    />
  </div>
</template>
